/**
 * 엔티티 렌더 레이어 — sim 상태(모험가/적/사천왕/묘비)를 id→메시로 동기화.
 * 위치의 원천은 sim(x,z). client는 y=heightAt + 흔들림(view)만 더한다. 길은 이동 중 페인팅.
 */
import * as THREE from 'three';
import { GRADE_COLOR, type EntityView } from '@villager/shared';
import { heightAt, type World } from '@villager/sim-core';

type PaintFn = (x: number, z: number, r: number, a: number) => void;

function makeAdvMesh(color: number): THREE.Group {
  const g = new THREE.Group();
  const body = new THREE.Mesh(
    new THREE.CylinderGeometry(0.55, 0.7, 1.5, 7),
    new THREE.MeshLambertMaterial({ color }),
  );
  body.position.y = 0.95;
  g.add(body);
  const head = new THREE.Mesh(
    new THREE.SphereGeometry(0.5, 8, 7),
    new THREE.MeshLambertMaterial({ color: 0xe8cfa8 }),
  );
  head.position.y = 2.05;
  g.add(head);
  return g;
}

function makeMob(named: boolean): THREE.Mesh {
  return new THREE.Mesh(
    named ? new THREE.ConeGeometry(1.3, 2.8, 5) : new THREE.SphereGeometry(0.85, 6, 5),
    new THREE.MeshLambertMaterial({ color: named ? 0x8a3a5a : 0x5a3a4a }),
  );
}

function makeBoss(): THREE.Mesh {
  return new THREE.Mesh(
    new THREE.ConeGeometry(2.4, 5.5, 5),
    new THREE.MeshLambertMaterial({ color: 0x7a2c4a }),
  );
}

function makeTomb(x: number, z: number): THREE.Mesh {
  const tomb = new THREE.Mesh(
    new THREE.BoxGeometry(0.8, 1.3, 0.3),
    new THREE.MeshLambertMaterial({ color: 0x8a8a80 }),
  );
  tomb.position.set(x, heightAt(x, z) + 0.65, z);
  tomb.rotation.y = Math.random();
  return tomb;
}

export class EntityLayer {
  private adv = new Map<number, THREE.Group>();
  private mob = new Map<number, THREE.Mesh>();
  private boss = new Map<number, THREE.Mesh>();
  private tombCount = 0;

  constructor(private worldGroup: THREE.Group) {}

  sync(w: World, t: number, paintPath: PaintFn): void {
    // ── 모험가 ──
    const seenAdv = new Set<number>();
    for (const g of w.groups) {
      const party = g.members.length > 1;
      g.members.forEach((a, idx) => {
        seenAdv.add(a.id);
        let mesh = this.adv.get(a.id);
        if (!mesh) {
          mesh = makeAdvMesh(GRADE_COLOR[Math.min(6, a.grade)]!);
          mesh.userData['px'] = a.pos.x;
          mesh.userData['pz'] = a.pos.z;
          this.worldGroup.add(mesh);
          this.adv.set(a.id, mesh);
        }
        const body = mesh.children[0] as THREE.Mesh;
        (body.material as THREE.MeshLambertMaterial).color.setHex(GRADE_COLOR[Math.min(6, a.grade)]!);
        const y = heightAt(a.pos.x, a.pos.z) + Math.abs(Math.sin(t * 9 + idx)) * 0.12;
        const dx = a.pos.x - mesh.userData['px'];
        const dz = a.pos.z - mesh.userData['pz'];
        if (Math.hypot(dx, dz) > 0.001) {
          mesh.rotation.y = Math.atan2(dx, dz);
          paintPath(a.pos.x, a.pos.z, idx === 0 ? 4.5 : 3.5, idx === 0 ? 0.028 : 0.02);
        }
        mesh.position.set(a.pos.x, y, a.pos.z);
        mesh.userData['px'] = a.pos.x;
        mesh.userData['pz'] = a.pos.z;
        if (idx === 0 && party && !mesh.userData['flag']) {
          const flag = new THREE.Mesh(
            new THREE.ConeGeometry(0.4, 1, 4),
            new THREE.MeshBasicMaterial({ color: 0xe8b34b }),
          );
          flag.position.y = 3.1;
          mesh.add(flag);
          mesh.userData['flag'] = flag;
        }
      });
    }
    for (const [id, mesh] of this.adv) {
      if (!seenAdv.has(id)) {
        this.worldGroup.remove(mesh);
        this.adv.delete(id);
      }
    }

    // ── 잡몹/네임드 ──
    const seenMob = new Set<number>();
    for (const m of w.mobs) {
      seenMob.add(m.id);
      let mesh = this.mob.get(m.id);
      if (!mesh) {
        mesh = makeMob(m.named);
        this.worldGroup.add(mesh);
        this.mob.set(m.id, mesh);
      }
      mesh.position.set(m.pos.x, heightAt(m.pos.x, m.pos.z) + 0.9, m.pos.z);
    }
    for (const [id, mesh] of this.mob) {
      if (!seenMob.has(id)) {
        this.worldGroup.remove(mesh);
        this.mob.delete(id);
      }
    }

    // ── 사천왕 (죽으면 숨김) ──
    for (const b of w.bosses) {
      let mesh = this.boss.get(b.id);
      if (!mesh) {
        mesh = makeBoss();
        mesh.position.set(b.pos.x, heightAt(b.pos.x, b.pos.z) + 2.7, b.pos.z);
        this.worldGroup.add(mesh);
        this.boss.set(b.id, mesh);
      }
      mesh.visible = !b.dead;
    }

    // ── 묘비 (증가분만 추가) ──
    while (this.tombCount < w.tombs.length) {
      const tb = w.tombs[this.tombCount]!;
      this.worldGroup.add(makeTomb(tb.pos.x, tb.pos.z));
      this.tombCount++;
    }
  }

  /**
   * 네트워크 모드 동기화 — 서버 스냅샷(기본 10Hz)의 목표 위치로 부드럽게 보간한다.
   * 위치의 원천은 서버이며 클라는 절대 예측/변경하지 않는다.
   */
  syncNet(ents: readonly EntityView[], dt: number, t: number, paintPath: PaintFn): void {
    const seenAdv = new Set<number>();
    const seenMob = new Set<number>();
    const seenBoss = new Set<number>();
    const k = Math.min(1, dt * 8); // 보간 계수

    for (const e of ents) {
      if (e.kind === 'adv') {
        seenAdv.add(e.id);
        let mesh = this.adv.get(e.id);
        if (!mesh) {
          mesh = makeAdvMesh(GRADE_COLOR[Math.min(6, e.grade ?? 0)]!);
          mesh.position.set(e.x, heightAt(e.x, e.z), e.z);
          mesh.userData['px'] = e.x;
          mesh.userData['pz'] = e.z;
          this.worldGroup.add(mesh);
          this.adv.set(e.id, mesh);
        }
        const body = mesh.children[0] as THREE.Mesh;
        (body.material as THREE.MeshLambertMaterial).color.setHex(
          GRADE_COLOR[Math.min(6, e.grade ?? 0)]!,
        );
        const nx = mesh.position.x + (e.x - mesh.position.x) * k;
        const nz = mesh.position.z + (e.z - mesh.position.z) * k;
        const dx = nx - mesh.userData['px'];
        const dz = nz - mesh.userData['pz'];
        if (Math.hypot(dx, dz) > 0.001) {
          mesh.rotation.y = Math.atan2(dx, dz);
          paintPath(nx, nz, 4.2, 0.026);
        }
        mesh.position.set(nx, heightAt(nx, nz) + Math.abs(Math.sin(t * 9)) * 0.12, nz);
        mesh.userData['px'] = nx;
        mesh.userData['pz'] = nz;
        if (e.party && !mesh.userData['flag']) {
          const flag = new THREE.Mesh(
            new THREE.ConeGeometry(0.4, 1, 4),
            new THREE.MeshBasicMaterial({ color: 0xe8b34b }),
          );
          flag.position.y = 3.1;
          mesh.add(flag);
          mesh.userData['flag'] = flag;
        }
      } else if (e.kind === 'mob') {
        seenMob.add(e.id);
        let mesh = this.mob.get(e.id);
        if (!mesh) {
          mesh = makeMob(!!e.named);
          mesh.position.set(e.x, heightAt(e.x, e.z) + 0.9, e.z);
          this.worldGroup.add(mesh);
          this.mob.set(e.id, mesh);
        }
        const nx = mesh.position.x + (e.x - mesh.position.x) * k;
        const nz = mesh.position.z + (e.z - mesh.position.z) * k;
        mesh.position.set(nx, heightAt(nx, nz) + 0.9, nz);
      } else {
        seenBoss.add(e.id);
        let mesh = this.boss.get(e.id);
        if (!mesh) {
          mesh = makeBoss();
          mesh.position.set(e.x, heightAt(e.x, e.z) + 2.7, e.z);
          this.worldGroup.add(mesh);
          this.boss.set(e.id, mesh);
        }
        mesh.visible = true;
      }
    }

    // 뷰포트를 벗어났거나 사라진 엔티티는 제거
    for (const [id, mesh] of this.adv) {
      if (!seenAdv.has(id)) {
        this.worldGroup.remove(mesh);
        this.adv.delete(id);
      }
    }
    for (const [id, mesh] of this.mob) {
      if (!seenMob.has(id)) {
        this.worldGroup.remove(mesh);
        this.mob.delete(id);
      }
    }
    for (const [id, mesh] of this.boss) {
      if (!seenBoss.has(id)) mesh.visible = false;
    }
  }
}
